<template>
  <v-card>
    <v-card-title class="primary">
      <span class="headline white--text">{{title}}</span>
      <v-spacer/>
      <Icon
      dataTestId="contact-close"
      iconColor="white"
      icon="mdi-close"
      :small="false"
      :large="true"
      :tooltipText="$t('close')"
      @icon-clicked="$emit('close')"
      />
    </v-card-title>
    <v-card-text>
      <FormWrapper
        ref="form"
        formRef="contactForm"
        :buttonText="submitBtnText"
        @submit="saveChangesPressed"
      >
        <v-container fluid grid-list-xl>
          <v-row class="mt-1">
            <v-col cols="12">
              <span class="title">{{ $t('personalInfo') }}</span>
            </v-col>
          </v-row>
          <v-row class="mt-n3">
            <v-col cols="12" md="6" :lg="isCompanyInfoContact ? '6' : '4'">
              <v-text-field
              v-model="contact.person.firstName"
              data-testid="contact-firstname"
              ref="firstField"
              color="black"
              maxLength="64"
              counter="64"
              :label="$t('firstName')"
              :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="6" :lg="isCompanyInfoContact ? '6' : '4'">
              <v-text-field
              v-model="contact.person.lastName"
              data-testid="contact-lastname"
              maxLength="64"
              counter="64"
              color="black"
              :label="$t('lastName')"
              :rules="[rules.required]"/>
            </v-col>
            <v-col cols="12" md="6" lg="4" v-if="!isCompanyInfoContact">
              <v-text-field
              v-model="contact.position"
              data-testid="contact-position"
              maxLength="32"
              counter="32"
              color="black"
              :label="$t('position')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <span class="title">{{ $t('contactInfo') }}</span>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6" lg="4">
              <v-text-field
              v-model="contact.person.phoneNumber"
              v-mask="phoneMask"
              data-testid="contact-phonenumber"
              color="black"
              :label="$t('phoneNumber')"
              :rules="isCompanyInfoContact ? [rules.required, rules.optionalPhoneNumber] : [rules.optionalPhoneNumber]"
              />
            </v-col>
            <v-col cols="12" md="6" lg="8">
              <v-text-field
              v-model="contact.person.email"
              data-testid="contact-email"
              maxLength="128"
              counter="128"
              color="black"
              :label="$t('email')"
              :rules="isCompanyInfoContact ? [rules.required, rules.email] : [rules.optionalEmail]">
                <template #append>
                  <Icon
                  v-if="invalidEmail"
                  icon="mdi-alert"
                  iconColor="error"
                  :tooltipText="suppressionTooltip"
                  :small="false"
                  @icon-clicked="removeSuppressionHold"
                  />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <span v-if="!isCompanyInfoContact">
            <v-row>
              <v-col cols="12" class="d-flex align-center">
                <span class="title">{{ $t('preferences') }}</span>
                <Icon
                v-if="!hasValidEmail"
                icon="mdi-information"
                iconColor="info"
                :tooltipText="$t('emailRequiredForPreferences')"
                :small="false"
                class="ml-1 mt-n1"
                />
              </v-col>
            </v-row>
            <v-row class="mt-n5">
              <v-col cols="12" md="6">
                <v-checkbox
                v-model="contact.isDefault"
                data-testid="contact-default-check"
                color="primary"
                :label="$t('defaultContact')"/>
                <v-checkbox
                v-model="contact.isInsuranceContact"
                color="primary"
                :disabled="!hasValidEmail"
                :label="$t('insuranceContact')"/>
                <v-checkbox
                v-model="contact.receivesSettlementReport"
                color="primary"
                :disabled="!hasValidEmail"
                :label="$t('settlementReportRecipient')"/>
                <v-checkbox
                v-model="contact.isEudrContact"
                color="primary"
                :disabled="!hasValidEmail"
                :label="$t('eudrContact')"/>
                <v-checkbox
                v-if="!dtrDisabled"
                v-model="receivesDailyScaleData"
                color="primary"
                :disabled="!hasValidEmail"
                :label="$t('dailyScaleDataRecipient')"/>
              </v-col>
              <v-col cols="12" md="6" class="mt-md-4" v-if="!dtrDisabled">
                <v-card class="pl-4 pt-3">
                  <v-radio-group
                  v-model="contact.dailyTicketReportPreference"
                  :label="$t('dailyTicketDeliveryFormat')"
                  :disabled="!hasValidEmail"
                  class="mt-0">
                      <v-radio
                      v-for="item in DeliveryFormat.enums"
                      :key="item.value"
                      :label="item.name"
                      :value="item.value"/>
                  </v-radio-group>
                </v-card>
              </v-col>
            </v-row>
          </span>
        </v-container>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { mask } from 'vue-the-mask'
import fieldRules from '@/utils/rules.js'
import { cloneDeep } from 'lodash'
import { EmailStatus, EmailSuppressionType, DeliveryFormat, DailyScaleDataFormat } from '@/utils/Enumerations'
import { mapGetters } from 'vuex'

const MAX_REPORT_RECIPIENTS = 5

export default {
  name: 'ContactForm',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue')
  },

  props: {
    propContact: {
      type: Object,
      default: undefined
    },
    isCompanyInfoContact: {
      type: Boolean,
      default: false
    },
    dtrDisabled: {
      type: Boolean,
      default: false
    },
    siblingContacts: {
      type: Array,
      default: null
    }
  },

  directives: {
    mask
  },

  computed: {
    submitBtnText () {
      return this.propContact && !this.isContactEmpty(this.propContact.person) ? this.$t('saveChanges') : this.$t('newContact')
    },

    title () {
      return this.propContact && !this.isContactEmpty(this.propContact.person) ? this.$t('editingContact') : this.$t('creatingContact')
    },

    invalidEmail () {
      return this.contact.person.emailStatus === EmailStatus.Invalid.value && !this.emailModified
    },

    hasValidEmail () {
      return this.rules.email(this.contact.person.email) === true
    },

    emailModified () {
      return !this.propContact || this.propContact.person.email !== this.contact.person.email
    },

    suppressionTooltip () {
      const base = EmailSuppressionType.fromInt(this.contact.person.emailSuppressionType)
      return this.suppressionRequiresEmailChange
        ? base
        : `${base} - ${this.$t('clickToRemoveHold')}`
    },

    suppressionRequiresEmailChange () {
      return this.contact.person.emailSuppressionType === EmailSuppressionType.Invalid.value || this.contact.person.emailSuppressionType === EmailSuppressionType.Block.value
    },

    otherContacts () {
      if (this.siblingContacts === null) return null
      return this.siblingContacts.filter(c => c !== this.propContact)
    },

    receivesDailyScaleData: {
      get () {
        return this.contact.dailyScaleDataPreference !== DailyScaleDataFormat.None.value
      },
      set (value) {
        this.contact.dailyScaleDataPreference = value ? DailyScaleDataFormat.Csv.value : DailyScaleDataFormat.None.value
      }
    }
  },

  watch: {
    hasValidEmail (val) {
      if (!val) {
        this.savedEmailPreferences = {
          isInsuranceContact: this.contact.isInsuranceContact,
          receivesSettlementReport: this.contact.receivesSettlementReport,
          isEudrContact: this.contact.isEudrContact,
          dailyScaleDataPreference: this.contact.dailyScaleDataPreference,
          dailyTicketReportPreference: this.contact.dailyTicketReportPreference
        }
        this.contact.isInsuranceContact = false
        this.contact.receivesSettlementReport = false
        this.contact.isEudrContact = false
        this.contact.dailyScaleDataPreference = DailyScaleDataFormat.None.value
        this.contact.dailyTicketReportPreference = DeliveryFormat.None.value
      } else if (this.savedEmailPreferences) {
        this.contact.isInsuranceContact = this.savedEmailPreferences.isInsuranceContact
        this.contact.receivesSettlementReport = this.savedEmailPreferences.receivesSettlementReport
        this.contact.isEudrContact = this.savedEmailPreferences.isEudrContact
        this.contact.dailyScaleDataPreference = this.savedEmailPreferences.dailyScaleDataPreference
        this.contact.dailyTicketReportPreference = this.savedEmailPreferences.dailyTicketReportPreference
        this.savedEmailPreferences = null
      }
    }
  },

  created () {
    if (this.propContact) {
      this.contact = cloneDeep(this.propContact)
    }
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.firstField.focus()
    }, 0)
  },

  data: () => ({
    EmailStatus,
    EmailSuppressionType,
    DeliveryFormat,
    phoneMask: '(###)-###-####',
    savedEmailPreferences: null,
    rules: fieldRules.rules,
    contact: {
      isDefault: false,
      isInsuranceContact: false,
      isEudrContact: false,
      dailyScaleDataPreference: DailyScaleDataFormat.None.value,
      dailyTicketReportPreference: DeliveryFormat.None.value,
      position: '',
      person: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: ''
      }
    }
  }),

  methods: {
    ...mapGetters('user', ['companyInfo']),

    saveChangesPressed () {
      const info = this.companyInfo()
      if (info.insuranceContact == null && this.contact.isInsuranceContact) {
        this.setSnackError(this.$t('setInsuranceComplianceUser'))
        return
      }

      if (!this.$refs.form.$refs.contactForm.validate()) {
        return
      }

      if (!this.reportRecipientCapsRespected()) {
        return
      }

      if (this.emailModified) this.contact.person.emailStatus = EmailStatus.Active.value

      if (!this.contact.person.email) this.contact.person.email = null
      if (!this.contact.person.phoneNumber) this.contact.person.phoneNumber = null

      if (this.propContact) {
        this.$emit('contact-updated', this.contact)
      } else {
        this.$emit('contact-created', this.contact)
      }
    },

    reportRecipientCapsRespected () {
      const others = this.otherContacts
      if (others === null) return true

      if (this.contact.receivesSettlementReport &&
        others.filter(c => c.receivesSettlementReport).length >= MAX_REPORT_RECIPIENTS) {
        this.setSnackError(this.$t('maxSettlementReportRecipients', { max: MAX_REPORT_RECIPIENTS }))
        return false
      }

      if (this.contact.dailyTicketReportPreference !== DeliveryFormat.None.value &&
        others.filter(c => c.dailyTicketReportPreference !== DeliveryFormat.None.value).length >= MAX_REPORT_RECIPIENTS) {
        this.setSnackError(this.$t('maxDailyTicketReportRecipients', { max: MAX_REPORT_RECIPIENTS }))
        return false
      }

      if (this.receivesDailyScaleData &&
        others.filter(c => c.dailyScaleDataPreference !== DailyScaleDataFormat.None.value).length >= MAX_REPORT_RECIPIENTS) {
        this.setSnackError(this.$t('maxDailyScaleDataRecipients', { max: MAX_REPORT_RECIPIENTS }))
        return false
      }

      return true
    },

    isContactEmpty (contact) {
      return !contact.firstName &&
      !contact.lastName &&
      !contact.email &&
      !contact.phoneNumber
    },

    removeSuppressionHold () {
      if (this.suppressionRequiresEmailChange) {
        this.setSnackError(this.$t('emailAddressRequiresChange'))
        return
      }

      this.contact.person.emailStatus = EmailStatus.Active.value
    }
  }
}
</script>
